import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface RoiPerEmailDomainData {
  email_domain: string;
  leads: number;
  anfos_30d: number;
  revenue_30d: number;
  total_cost: number;
  conversion_rate_30d: number | null;
  roi30: number | null;
}

const RoiPerEmailDomain: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<RoiPerEmailDomainData[]>([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof RoiPerEmailDomainData>('leads');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RoiPerEmailDomainData,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchEmailDomainData = async (
    filterState: IFilterState,
  ): Promise<RoiPerEmailDomainData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<RoiPerEmailDomainData>('/api/vnr/roi-per-email-domain', params) as Promise<
      RoiPerEmailDomainData[]
    >;
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        const result = await fetchEmailDomainData(filterState);
        const processedResult = result.map((item) => ({
          ...item,
          leads: parseInt(String(item.leads), 10) || 0,
          anfos_30d: parseInt(String(item.anfos_30d), 10) || 0,
          revenue_30d: parseFloat(String(item.revenue_30d)) || 0,
          total_cost: parseFloat(String(item.total_cost)) || 0,
          conversion_rate_30d:
            item.conversion_rate_30d !== null ? parseFloat(String(item.conversion_rate_30d)) : null,
          roi30: item.roi30 !== null ? parseFloat(String(item.roi30)) : null,
        }));
        setData(processedResult);
      } catch (error) {
        console.error('Error fetching ROI by email domain data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.startDate,
    filterState.endDate,
  ]);

  const computeSummary = (data: RoiPerEmailDomainData[]): RoiPerEmailDomainData => {
    if (!data.length) {
      return {
        email_domain: 'Insgesamt',
        leads: 0,
        anfos_30d: 0,
        revenue_30d: 0,
        total_cost: 0,
        conversion_rate_30d: null,
        roi30: null,
      };
    }

    const summary = {
      email_domain: 'Insgesamt',
      leads: 0,
      anfos_30d: 0,
      revenue_30d: 0,
      total_cost: 0,
      conversion_rate_30d: null as number | null,
      roi30: null as number | null,
    };

    data.forEach((item) => {
      summary.leads += parseInt(String(item.leads), 10) || 0;
      summary.anfos_30d += parseInt(String(item.anfos_30d), 10) || 0;
      summary.revenue_30d += parseFloat(String(item.revenue_30d)) || 0;
      summary.total_cost += parseFloat(String(item.total_cost)) || 0;
    });

    summary.conversion_rate_30d =
      summary.leads > 0 ? (summary.anfos_30d / summary.leads) * 100 : null;
    summary.roi30 =
      summary.total_cost > 0 ? (summary.revenue_30d / summary.total_cost) * 100 : null;

    return summary;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Filter data based on search query
  const filteredData = data.filter((item) =>
    (item.email_domain || '').toString().toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const summary = computeSummary(filteredData);
  const sortedData = sortedRowInformation(filteredData, getComparator(order, orderBy));
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const allData = [...filteredData, summary];

  const formatLeadsWithPercentage = (value: number, total: number): React.ReactElement => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
      <>
        <strong>{formatNumber(value, 0)}</strong> | {formatNumber(percentage, 2)}%
      </>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          label="Domain suchen"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => exportToExcel(allData, 'VNR_ROI_Email_Domain')}
        >
          Export to Excel
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email_domain'}
                  direction={orderBy === 'email_domain' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'email_domain')}
                >
                  E-Mail Domain
                  {orderBy === 'email_domain' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'leads'}
                  direction={orderBy === 'leads' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'leads')}
                >
                  Leads
                  {orderBy === 'leads' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'anfos_30d'}
                  direction={orderBy === 'anfos_30d' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'anfos_30d')}
                >
                  ANFOs 30d
                  {orderBy === 'anfos_30d' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'revenue_30d'}
                  direction={orderBy === 'revenue_30d' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'revenue_30d')}
                >
                  Revenue 30d
                  {orderBy === 'revenue_30d' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'conversion_rate_30d'}
                  direction={orderBy === 'conversion_rate_30d' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'conversion_rate_30d')}
                >
                  Conversion Rate 30d
                  {orderBy === 'conversion_rate_30d' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'roi30'}
                  direction={orderBy === 'roi30' ? order : 'asc'}
                  onClick={(event) => handleRequestSort(event, 'roi30')}
                >
                  ROI (30d)
                  {orderBy === 'roi30' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.email_domain}</TableCell>
                <TableCell>{formatLeadsWithPercentage(row.leads, summary.leads)}</TableCell>
                <TableCell>{formatNumber(row.anfos_30d, 0)}</TableCell>
                <TableCell>{formatNumber(row.revenue_30d, 2)} €</TableCell>
                <TableCell>
                  {row.conversion_rate_30d !== null
                    ? formatNumber(row.conversion_rate_30d, 2) + '%'
                    : '-'}
                </TableCell>
                <TableCell>{row.roi30 !== null ? formatNumber(row.roi30, 2) + '%' : '-'}</TableCell>
              </TableRow>
            ))}
            {/* Summary row */}
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>{summary.email_domain}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(summary.leads, 0)}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {formatNumber(summary.anfos_30d, 0)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {formatNumber(summary.revenue_30d, 2)} €
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summary.conversion_rate_30d !== null
                  ? formatNumber(summary.conversion_rate_30d, 2) + '%'
                  : '-'}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summary.roi30 !== null ? formatNumber(summary.roi30, 2) + '%' : '-'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};

export default RoiPerEmailDomain;
